import { ChainId, ChainIdLegacy } from "@silo/types";
import { Array, Record, pipe } from "effect";

const ignorePhrases = [/\bscheduled\b/i, /\bdeprecation\b/i, /\bupgrade\b/i];

/**
 * Any title with these phrases is flagged
 */
const INCLUDED_SCOPES: Record<ChainId, RegExp[]> = {
  [ChainIdLegacy.ETHEREUM]: [/\beth mainnet\b/i, /\bethereum mainnet\b/i],
  [ChainIdLegacy.ARBITRUM]: [/\barb mainnet\b/i, /\barbitrum mainnet\b/i],
  [ChainIdLegacy.OPTIMISM]: [/\bopt mainnet\b/i, /\boptimism mainnet\b/i],
  [ChainIdLegacy.BASE]: [/\bbase mainnet\b/i],
  [ChainIdLegacy.LOCAL]: [],
  [ChainIdLegacy.ANVIL]: [],
};

const includedScopes = pipe(INCLUDED_SCOPES, Record.values, Array.flatten);

/**
 * After filtering out specific scopes, any title that still has these phrases is flagged
 */
const CATCH_ALL_SCOPES: Record<ChainId, RegExp[]> = {
  [ChainIdLegacy.ETHEREUM]: [/\beth\b/i, /\bethereum\b/i],
  [ChainIdLegacy.OPTIMISM]: [/\boptimism\b/i, /\bopt\b/i],
  [ChainIdLegacy.BASE]: [/\bbase\b/i],
  [ChainIdLegacy.ARBITRUM]: [/\barb\b/i, /\barbitrum\b/i],
  [ChainIdLegacy.LOCAL]: [],
  [ChainIdLegacy.ANVIL]: [],
};

const catchAllScopes = pipe(CATCH_ALL_SCOPES, Record.values, Array.flatten);

/**
 * For any phrases with spaces, use `.*` to match variable characters in between
 * the words.
 */
const dangerPhrases = [
  /\bdegraded.* performance\b/i,
  /\boutage\b/i,
  /\b(elevated|increased).* (error rates|errors|delay|latency)\b/i,
  /\binstability\b/i,
  /\bhalted\b/i,
  /\bfailure\b/i,
  /\bunavailability\b/i,
];

/**
 * Any singular provider scope - if any of these show up in an incident title,
 * then we assume the incident is scoped to those products/services, and is not
 * a general outage across all chains/products.
 */

/**
 * https://www.infura.io/networks?blockchain=all
 */
const infuraExcludedScopes = [
  /\blinea\b/i,
  /\bpolygon\b/i,
  /\baurora\b/i,
  /\bstarknet\b/i,
  /\bavalanche\b/i,
  /\bcelo\b/i,
  /\bpalm\b/i,
  /\bbase\b/i,
  /\bgas api\b/i,
  /\btrace api\b/i,
  /\bnear\b/i,
  /\bipfs\b/i,
  /\bwebpage\b/i,
  /\bsepolia\b/i,
  /\bgoerli\b/i,
  /\bblast\b/i,
  /\buser\b/i,
  /\bdashboard\b/i,
  /\bwebsite\b/i,
  /\bholesky\b/i,
];

/**
 * https://www.alchemy.com/
 */
const alchemyExcludedScopes = [
  // APIs
  /\bsupernode\b/i,
  /\bnft api\b/i,
  /\bwebhooks\b/i,
  /\bcustom webhooks\b/i,
  /\bsmart websockets\b/i,
  /\btoken api\b/i,
  /\btransfers api\b/i,
  /\btransact\b/i,
  /\btransaction simulation\b/i,
  /\baccount kit\b/i,
  /\bgas manager api\b/i,
  /\bbundler apr\b/i,
  /\bspearmint\b/i,
  /\bsubgraphs\b/i,

  // chains
  /\bmonad\b/i,
  /\bpolygon\b/i,
  /\bzkevm\b/i,
  /\bsolana\b/i,
  /\bastar\b/i,
  /\bstarknet\b/i,
  /\bbase\b/i,
  /\bsepolia\b/i,
  /\bgoerli\b/i,
  /\bflow\b/i,
  /\bholesky\b/i,
  /\bopbnb\b/i,
  /\bbnb\b/i,

  // other
  /\bsign.*ups?\b/i,
];

const excludedScopes = [...infuraExcludedScopes, ...alchemyExcludedScopes];

export function isCriticalIncident(title: string): boolean {
  /**
   * If title includes any of the ignore phrases, we ignore it because it is
   * some kind of scheduled maintenance or upgrade.
   */
  if (ignorePhrases.some((phrase) => phrase.test(title))) {
    return false;
  }

  /**
   * If the title is scoped to an included scope, flag it.
   */
  if (includedScopes.some((scope) => scope.test(title))) {
    return true;
  }

  /**
   * If the title is scoped to a specific chain or service, then ignore it.
   */
  if (excludedScopes.some((scope) => scope.test(title))) {
    return false;
  }

  /**
   * If title includes any chains of interest, automatically flag it
   */
  if (catchAllScopes.some((inclusion) => inclusion.test(title))) {
    return true;
  }

  /**
   * At this point, if the title does not include a danger phrase, we can ignore
   * it.
   */
  if (!dangerPhrases.some((phrase) => phrase.test(title))) {
    return false;
  }

  /**
   * At this point, the title includes a danger phrase, and is not scoped to
   * specific service/chain. It must be a general incident that spans multiple
   * chains/services, so we flag it.
   */
  return true;
}
